import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon, ScaleIcon, ShieldExclamationIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { fairLendingApi } from '../lib/api';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useTeamId } from '../hooks/useTeamId';
import RiskBadge from '../components/fair-lending/RiskBadge';
import AnalysisModal from '../components/fair-lending/AnalysisModal';
import FldAnalysisModal from '../components/fld/FldAnalysisModal';
import { PageHeader, StatusBadge, LoadingSpinner } from '../components/ui';

export default function FairLendingDashboardPage() {
  const navigate = useNavigate();
  const { teamId } = useTeamId();
  const { hasFeature, isLoading: featuresLoading } = useFeatureAccess();
  const [modalOpen, setModalOpen] = useState(false);
  const [fldModalOpen, setFldModalOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['fair-lending-dashboard', teamId],
    queryFn: async () => {
      const res = await fairLendingApi.getDashboard(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  // Enterprise gate — uses feature API, not stale auth store plan
  if (featuresLoading) return <LoadingSpinner className="py-16" />;
  if (!hasFeature('module_fair_lending')) {
    return (
      <div className="text-center py-16">
        <ScaleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-xl font-semibold text-gray-900">Fair Lending Analysis</h2>
        <p className="mt-2 text-gray-600 max-w-md mx-auto">
          Fair Lending Analysis is available on the Enterprise plan.
          Analyze HMDA data for disparate impact in denial rates, pricing, and geographic distribution.
        </p>
        <Link
          to="/billing"
          className="mt-4 inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700"
        >
          Upgrade to Enterprise
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load dashboard data. Please try again.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Denial & Pricing"
        description="Statistical analysis of HMDA data for disparate impact"
        actions={
          <div className="flex gap-3">
            <button
              onClick={() => setFldModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700"
            >
              <TableCellsIcon className="h-4 w-4" />
              Run FLD Analysis
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4" />
              Run Standard Analysis
            </button>
          </div>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <SummaryCard
          title="Overall Risk"
          value={data?.overallRiskLevel ? <RiskBadge level={data.overallRiskLevel} /> : <span className="text-gray-400">No data</span>}
        />
        <SummaryCard
          title="Open Findings"
          value={<span className="text-2xl font-bold text-gray-900">{data?.openFindings ?? 0}</span>}
        />
        <SummaryCard
          title="Analyses Run"
          value={<span className="text-2xl font-bold text-gray-900">{data?.totalAnalyses ?? 0}</span>}
        />
        <SummaryCard
          title="Last Analysis"
          value={
            data?.lastAnalysisDate
              ? <span className="text-sm text-gray-700">{new Date(data.lastAnalysisDate).toLocaleDateString()}</span>
              : <span className="text-gray-400">Never</span>
          }
        />
      </div>

      {/* Recent analyses table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Analyses</h2>
        </div>
        {data?.recentAnalyses?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Types</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applications</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risk</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Findings</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.recentAnalyses.map((a: any) => (
                  <tr
                    key={a.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(
                      a.analysisTypes?.includes('FLD')
                        ? `/fair-lending/fld/${a.id}`
                        : `/fair-lending/analyses/${a.id}`,
                    )}
                  >
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {new Date(a.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {a.validationFileName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {a.analysisTypes.map((t: string) => t.replace(/_/g, ' ')).join(', ')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {a.totalApplications.toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      <RiskBadge level={a.overallRiskLevel} />
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {a.findingsCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <ScaleIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 text-sm text-gray-500">
              No analyses yet. Run your first fair lending analysis to get started.
            </p>
          </div>
        )}
      </div>

      {/* Content Screening Summary */}
      {(data?.contentScreening?.documentsScreened > 0 || data?.contentScreening?.openFindings > 0) && (
        <div className="bg-white rounded-lg shadow mt-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldExclamationIcon className="h-5 w-5 text-ocean-600" />
              <h2 className="text-lg font-semibold text-gray-900">Content Screening</h2>
            </div>
            <Link
              to="/fair-lending/content-findings"
              className="text-sm font-medium text-ocean-600 hover:text-ocean-700"
            >
              View All
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-6 py-4 border-b border-gray-100">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Documents Screened</p>
              <p className="mt-1 text-xl font-bold text-gray-900">{data.contentScreening.documentsScreened}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">High Findings</p>
              <p className="mt-1 text-xl font-bold text-red-600">{data.contentScreening.highFindings}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Medium Findings</p>
              <p className="mt-1 text-xl font-bold text-yellow-600">{data.contentScreening.mediumFindings}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Open Findings</p>
              <p className="mt-1 text-xl font-bold text-gray-900">{data.contentScreening.openFindings}</p>
            </div>
          </div>

          {data.contentScreening.recentFindings?.length > 0 && (
            <div className="px-6 py-3 divide-y divide-gray-50">
              {data.contentScreening.recentFindings.map((f: any) => (
                <div key={f.id} className="py-2.5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <StatusBadge variant={f.severity === 'HIGH' ? 'danger' : f.severity === 'MEDIUM' ? 'warning' : 'success'}>
                        {f.severity}
                      </StatusBadge>
                      <span className="text-xs text-gray-500">
                        {f.category.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-gray-700 truncate">&ldquo;{f.phrase}&rdquo;</p>
                    {f.documentName && (
                      <p className="text-xs text-gray-400">{f.documentName}</p>
                    )}
                  </div>
                  <span className="shrink-0 text-xs text-gray-400">
                    {new Date(f.createdAt).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AnalysisModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={(id) => navigate(`/fair-lending/analyses/${id}`)}
      />

      <FldAnalysisModal
        isOpen={fldModalOpen}
        onClose={() => setFldModalOpen(false)}
        onSuccess={(id) => navigate(`/fair-lending/fld/${id}`)}
      />
    </div>
  );
}

function SummaryCard({ title, value }: { title: string; value: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg shadow px-5 py-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{title}</p>
      {value}
    </div>
  );
}
